<template>
  <div v-for="action in interrupt.value.action_requests" :key="action.name" class="interrupt-block__action">
    <div class="interrupt-block__action-name">{{ formatKey(action.name) }}</div>
    <div v-if="hasArgs(action)" class="interrupt-block__args">{{ formatArgs(action.args) }}</div>
  </div>

  <!-- Default: decision buttons -->
  <div v-if="!state.mode" class="interrupt-block__decisions">
    <BbBtn
      v-for="decision in allowedDecisions"
      :key="decision"
      size="small"
      :type="decision === 'reject' ? 'secondary' : 'primary'"
      @click="handleDecision(decision)"
    >
      {{ capitalize(decision) }}
    </BbBtn>
  </div>

  <!-- Reject form -->
  <div v-else-if="state.mode === 'reject'" class="interrupt-block__form">
    <textarea v-model="state.reason" placeholder="Reason for rejection..." class="interrupt-block__textarea" rows="3" />
    <div class="interrupt-block__form-actions">
      <BbBtn size="small" type="secondary" @click="cancelMode">Cancel</BbBtn>
      <BbBtn
        size="small"
        type="destructive"
        @click="submitDecision(buildDecisions({ type: 'reject', message: state.reason }))"
      >
        Confirm Rejection
      </BbBtn>
    </div>
  </div>

  <!-- Edit form -->
  <div v-else-if="state.mode === 'edit'" class="interrupt-block__form">
    <textarea
      v-model="state.editedArgs"
      class="interrupt-block__textarea interrupt-block__textarea--code"
      rows="8"
      spellcheck="false"
    />
    <div v-if="state.editError" class="interrupt-block__error">
      {{ state.editError }}
    </div>
    <div class="interrupt-block__form-actions">
      <BbBtn size="small" type="secondary" @click="cancelMode">Cancel</BbBtn>
      <BbBtn size="small" type="primary" @click="submitEdit">Submit Edit</BbBtn>
    </div>
  </div>

  <!-- Respond form -->
  <div v-else-if="state.mode === 'respond'" class="interrupt-block__form">
    <textarea v-model="state.message" placeholder="Your message..." class="interrupt-block__textarea" rows="3" />
    <div class="interrupt-block__form-actions">
      <BbBtn size="small" type="secondary" @click="cancelMode">Cancel</BbBtn>
      <BbBtn
        size="small"
        type="primary"
        @click="submitDecision(buildDecisions({ type: 'respond', message: state.message }))"
      >
        Send
      </BbBtn>
    </div>
  </div>
</template>

<script>
import { computed, reactive } from 'vue';

import useConversations from '~/composables/useConversations';
import { formatKey } from '~/utils/format';

// The HITL tool-review half of what used to be InterruptBlockSSE.vue's single component.
// The image-gen half is ImageStyleChoiceInterrupt.vue. One instance of this component per
// interrupt (via InterruptBlockSSE's v-for), so form state (mode/reason/etc.) is local —
// no more manually-keyed-by-id reactive map.
export default {
  name: 'HitlReviewInterrupt',
  props: {
    interrupt: {
      type: Object,
      required: true,
    },
  },
  emits: ['submitted'],
  setup(props, { emit }) {
    const { resumeConversation } = useConversations();

    const state = reactive({
      mode: null,
      reason: '',
      editedArgs: '',
      editError: '',
      message: '',
    });

    const allowedDecisions = computed(
      () => props.interrupt.value?.review_configs?.[0]?.allowed_decisions ?? ['approve'],
    );

    const hasArgs = (action) => action.args && Object.keys(action.args).length > 0;

    const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

    const formatArgs = (args) => {
      if (!args || typeof args !== 'object') return '';
      return Object.entries(args)
        .map(([k, v]) => `${formatKey(k)}: ${typeof v === 'object' ? JSON.stringify(v) : v}`)
        .join('\n');
    };

    const cancelMode = () => {
      state.mode = null;
      state.editError = '';
    };

    // Builds a decisions array with one entry per action_request so backend
    // count matches hanging tool calls. Edit decisions use per-action name.
    const buildDecisions = (decision) => {
      const requests = props.interrupt.value?.action_requests ?? [];
      if (requests.length <= 1) return [decision];
      if (decision.type === 'edit') {
        return requests.map((action) => ({
          type: 'edit',
          editedAction: { name: action.name, args: decision.editedAction.args },
        }));
      }
      return requests.map(() => ({ ...decision }));
    };

    const handleDecision = (decision) => {
      if (decision === 'approve') {
        submitDecision(buildDecisions({ type: 'approve' }));
      } else if (decision === 'edit') {
        // not exposing decisions in from arcane-ai yet (for hitlRequest)
        const args = props.interrupt.value?.action_requests?.[0]?.args ?? {};
        state.editedArgs = JSON.stringify(args, null, 2);
        state.mode = 'edit';
      } else {
        state.mode = decision;
      }
    };

    // Emit before awaiting: collapses the parent's panel to its pill immediately
    // and unmounts this form (prevents double-submit too).
    const submitDecision = async (decisions) => {
      emit('submitted', decisions[0]?.type ?? '');
      await resumeConversation({ type: 'hitlResponse', decisions });
    };

    const submitEdit = () => {
      // given we are not renderin raw JSON to the user to edit anymore, this will need to be reworked if arcane-ai ever sends HITL interrupts with the edit action.
      let parsed;
      try {
        parsed = JSON.parse(state.editedArgs);
        state.editError = '';
      } catch {
        state.editError = 'Invalid JSON — fix before submitting.';
        return;
      }
      const actionName = props.interrupt.value?.action_requests?.[0]?.name ?? '';
      submitDecision(buildDecisions({ type: 'edit', editedAction: { name: actionName, args: parsed } }));
    };

    return {
      state,
      allowedDecisions,
      hasArgs,
      capitalize,
      formatKey,
      formatArgs,
      cancelMode,
      buildDecisions,
      handleDecision,
      submitDecision,
      submitEdit,
    };
  },
};
</script>

<style scoped lang="scss">
.interrupt-block__action {
  display: flex;
  flex-direction: column;
  gap: $bb-spacing-2;

  & + & {
    padding-top: $bb-spacing-3;
    border-top: 1px solid $bb-neutral-5;
  }
}

.interrupt-block__action-name {
  font-size: 12px;
  font-weight: 600;
  color: $bb-neutral-1;
}

.interrupt-block__args {
  font-size: 12px;
  white-space: pre-line;
  color: $bb-neutral-2;
  line-height: 1.6;
}

.interrupt-block__decisions {
  display: flex;
  flex-wrap: wrap;
  gap: $bb-spacing-2;
}

.interrupt-block__form {
  display: flex;
  flex-direction: column;
  gap: $bb-spacing-2;
}

.interrupt-block__form-actions {
  display: flex;
  gap: $bb-spacing-2;
  justify-content: flex-end;
}

.interrupt-block__textarea {
  width: 100%;
  border: 1px solid $bb-neutral-4;
  border-radius: 6px;
  padding: $bb-spacing-2 $bb-spacing-3;
  font-size: 13px;
  line-height: 1.5;
  resize: vertical;
  background-color: $bb-neutral-6;
  color: $bb-neutral-1;
  outline: none;
  box-sizing: border-box;

  &:focus {
    border-color: $bb-primary-3;
  }

  &--code {
    font-family: monospace;
    font-size: 12px;
  }
}

.interrupt-block__error {
  @include bb-regular-text-styles('sm');
  color: #ef4444;
}
</style>
